import React, { useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { detectLanguage, translate, getFiltersForGroups } from '../../actions/communication';
import { errorMessage, hideModal } from '../../actions/internal';
import CommunicationMessageModal from '../../components/communication/CommunicationMessageModal';
import { parseRecipients } from '../../utils/communication/recipientUtils';
import { createModalConfig } from '../../utils/communication/modalConfigUtils';

/**
 * CommunicationMessageModalContainer - Redux-connected container
 * Handles business logic, Redux state, and action dispatching
 */
const CommunicationMessageModalContainer = (props) => {
  const {
    // Redux actions
    onSendMessage,
    onSendMessageToGroups,
    detectLanguage,
    translate,
    errorMessage,
    hideModal,
    getFiltersForGroups,

    // Props passed from modal invocation
    message,
    originCards,
    originProgramContentEntries,
    player,
    onModalClose,
    onModalClassChange,
    forcePreview,
    config: customConfig,

    // Redux state
    filtersForGroups,
    filtersForGroupsReady,
    isSending,
    isTranslating,
    isDetecting,
    sendingComplete,
    translation,
  } = props;

  // Load filters on mount if not already loaded
  useEffect(() => {
    if (!filtersForGroupsReady) {
      getFiltersForGroups();
    }
  }, []);

  // Close modal when sending completes
  useEffect(() => {
    if (sendingComplete) {
      hideModal();
    }
  }, [sendingComplete, hideModal]);

  // Determine configuration based on context
  const config = useMemo(() => {
    if (customConfig) {
      return customConfig;
    }

    // Translate-only mode: player + message provided
    if (player && message) {
      return createModalConfig('TRANSLATE_ONLY');
    }

    // Library send mode: content pre-selected
    if (originCards?.length > 0) {
      return createModalConfig('LIBRARY_SEND_CARD');
    }

    if (originProgramContentEntries?.length > 0) {
      return createModalConfig('LIBRARY_SEND_TIP');
    }

    // Default mode
    return createModalConfig('DEFAULT');
  }, [customConfig, player, message, originCards, originProgramContentEntries]);

  // Unified send handler with business logic
  const handleSend = useCallback(
    ({
      messageBody,
      recipients,
      forIndividuals,
      ages,
      cards,
      programContentEntries,
      contentAvailable,
      translations,
    }) => {
      const parsedRecipients = parseRecipients(recipients, forIndividuals);

      if (forIndividuals) {
        onSendMessage({
          cardIds: cards.map((c) => c.value),
          contentAvailable,
          message: messageBody,
          playerIds: parsedRecipients.playerIds,
          programContentEntryIds: programContentEntries.map((p) => p.value),
          translations,
        });
      } else {
        onSendMessageToGroups({
          ages: ages.map((p) => p.value),
          cardIds: cards.map((c) => c.value),
          cohortIds: parsedRecipients.cohortIds,
          contentAvailable,
          groupIds: parsedRecipients.groupIds,
          message: messageBody,
          programContentEntryIds: programContentEntries.map((p) => p.value),
          subgroupIds: parsedRecipients.subgroupIds,
          translations,
        });
      }

      // Call onModalClose if provided, otherwise modal closes via sendingComplete
      if (typeof onModalClose === 'function') {
        onModalClose(false);
      }
    },
    [onSendMessage, onSendMessageToGroups, onModalClose]
  );

  // Handle cancel with optional callback
  const handleCancel = useCallback(() => {
    if (typeof onModalClose === 'function') {
      onModalClose(true);
    }
    hideModal();
  }, [onModalClose, hideModal]);

  return (
    <CommunicationMessageModal
      config={config}
      initialMessage={message}
      initialCards={originCards}
      initialProgramContentEntries={originProgramContentEntries}
      preselectedPlayer={player}
      filtersForGroups={filtersForGroups}
      filtersForGroupsReady={filtersForGroupsReady}
      isSending={isSending}
      isTranslating={isTranslating}
      isDetecting={isDetecting}
      translation={translation}
      onSend={handleSend}
      onCancel={handleCancel}
      onDetectLanguage={detectLanguage}
      onTranslate={translate}
      onError={errorMessage}
      onModalClassChange={onModalClassChange}
    />
  );
};

CommunicationMessageModalContainer.propTypes = {
  // Redux actions
  onSendMessage: PropTypes.func.isRequired,
  onSendMessageToGroups: PropTypes.func.isRequired,
  detectLanguage: PropTypes.func.isRequired,
  translate: PropTypes.func.isRequired,
  errorMessage: PropTypes.func.isRequired,
  hideModal: PropTypes.func.isRequired,
  getFiltersForGroups: PropTypes.func.isRequired,

  // Modal props
  message: PropTypes.string,
  originCards: PropTypes.array,
  originProgramContentEntries: PropTypes.array,
  player: PropTypes.shape({
    playerId: PropTypes.number,
    playerName: PropTypes.string,
    accountName: PropTypes.string,
  }),
  onModalClose: PropTypes.func,
  forcePreview: PropTypes.bool,
  config: PropTypes.object,

  // Redux state
  filtersForGroups: PropTypes.object.isRequired,
  filtersForGroupsReady: PropTypes.bool.isRequired,
  isSending: PropTypes.bool,
  isTranslating: PropTypes.bool,
  isDetecting: PropTypes.bool,
  sendingComplete: PropTypes.bool,
  translation: PropTypes.object,
};

CommunicationMessageModalContainer.defaultProps = {
  message: '',
  originCards: [],
  originProgramContentEntries: [],
  player: null,
  onModalClose: null,
  forcePreview: false,
  config: null,
  isSending: false,
  isTranslating: false,
  isDetecting: false,
  sendingComplete: false,
  translation: {},
};

const mapStateToProps = (state) => ({
  filtersForGroups: state.communicationFilters?.filtersForGroups?.filters || {},
  filtersForGroupsReady:
    state.communicationFilters?.filtersForGroups?.loaded &&
    !state.communicationFilters?.filtersForGroups?.isLoading,
  isSending: state.communication?.isSending,
  isTranslating: state.communication?.isTranslating,
  isDetecting: state.communication?.isDetecting,
  sendingComplete: state.communication?.sendingComplete,
  translation: state.communication?.translation || {},
});

export default connect(mapStateToProps, {
  hideModal,
  detectLanguage,
  translate,
  errorMessage,
  getFiltersForGroups,
})(CommunicationMessageModalContainer);
